import React, { useEffect, useState } from "react";
import "./ExternalComponentsLayers.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faFolder,
  faFolderOpen,
} from "@fortawesome/free-solid-svg-icons";
import externalJSON from "./external.json";
// import OpacitySelector from "./OpacitySelector"; // Ensure this is used if imported
import Switch from "@mui/material/Switch";
import { green } from "@mui/material/colors";
import { alpha, styled } from "@mui/material/styles";

const externalData = externalJSON;

// data from the external sources
var savedExternalLayers = [];

const ExternalComponentsLayers = () => {
  const [itemStates, setItemStates] = useState({});
  // "collapsed" here seems to function as "Main Folder Open" based on your icon logic
  const [mainFolderOpen, setMainFolderOpen] = useState(false);

  // Replaces collapsedCat1/Cat2 with a dynamic object
  const [categoryStates, setCategoryStates] = useState({});

  const toggleCategory = (categoryName) => {
    setCategoryStates((prev) => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  };

  const handleChange = (itemName) => {
    setItemStates((prevItemStates) => ({
      ...prevItemStates,
      [itemName.name]: {
        enabled: !prevItemStates[itemName.name]?.enabled,
        link: itemName.link,
        EPSG: itemName.EPSG,
        layer: itemName.name,
      },
    }));
  };

  // Styling for the switch in the triquetra Colors
  const GreenSwitch = styled(Switch)(({ theme }) => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: green[600],
      "&:hover": {
        backgroundColor: alpha(green[600], theme.palette.action.hoverOpacity),
      },
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: green[600],
    },
  }));

  // Checks the item states that hold all active externals
  useEffect(() => {
    Object.keys(itemStates).forEach((item) => {
      const { enabled, link, EPSG, layer } = itemStates[item];
      const sourceId = `${item}-source`;
      const layerId = `${item}-layer`;

      if (enabled) {
        if (!window.map.getSource(sourceId)) {
          savedExternalLayers.push({ Name: item, opacity: 1 });

          let tileUrl = "";

          if (link.toLowerCase().includes("wmts")) {
            // 1. Ensure we use EPSG:3857, as it is the official Copernicus standard
            const matrixSet = "EPSG:3857";

            // 2. Copernicus usually requires the TileMatrix to be prefixed with the projection
            const tileMatrix = "EPSG:3857:{z}";

            // 3. FIX: Strict Capitalization (Layer, Style, TileMatrixSet) and explicit Style=default
            tileUrl = `${link}?Service=WMTS&Request=GetTile&Version=1.0.0&Format=image/png&Layer=${layer}&Style=default&TileMatrixSet=${matrixSet}&TileMatrix=${tileMatrix}&TileCol={x}&TileRow={y}`;
          } else {
            // Standard WMS fallback
            tileUrl = `${link}?service=WMS&version=1.1.1&request=GetMap&layers=${layer}&styles=&bbox={bbox-epsg-3857}&width=512&height=512&srs=EPSG:3857&format=image/png&transparent=true`;
          }

          // 3. Proxy Logic
          let finalUrl = tileUrl;
          // Only proxy the strictly-restricted Copernicus domains, NOT Terrascope!
          if (
            link.includes("viewer.globalland") ||
            link.includes("land.copernicus.eu")
          ) {
            const encodedUrl = encodeURIComponent(tileUrl)
              .replace(/%7B/g, "{")
              .replace(/%7D/g, "}");
            finalUrl = `https://api.allorigins.win/raw?url=${encodedUrl}`;
          }

          window.map.addSource(sourceId, {
            id: sourceId,
            type: "raster",
            tiles: [finalUrl],
            tileSize: 512,
          });

          window.map.addLayer({
            id: layerId,
            type: "raster",
            source: sourceId,
            paint: {},
          });
        }
      } else {
        // Cleanup (Remains the same)
        if (window.map.getLayer(layerId)) window.map.removeLayer(layerId);
        if (window.map.getSource(sourceId)) window.map.removeSource(sourceId);
        savedExternalLayers = savedExternalLayers.filter(
          (l) => l.Name !== item,
        );
      }
    });
  }, [itemStates]);

  return (
    <div className="parent-group" id="external-parent-group">
      {/* --- MAIN FOLDER --- */}
      <div
        className="group-card"
        id={`group-card-external`}
        onClick={() => setMainFolderOpen(!mainFolderOpen)}
      >
        <div className="group-title">
          {mainFolderOpen ? (
            <FontAwesomeIcon icon={faFolderOpen} />
          ) : (
            <FontAwesomeIcon icon={faFolder} />
          )}{" "}
          External Sources
        </div>
        <div className="group-card-chevron">
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{
              transform: mainFolderOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "0.3s ease",
            }}
          />
        </div>
      </div>

      {/* --- MAIN CONTAINER --- */}
      <div
        className={`inner-group-container`}
        id={`inner-group-container-external`}
        // FIX: Control display via State, not ID/Helper function
        style={{ display: mainFolderOpen ? "block" : "none" }}
      >
        {Object.keys(externalData).map((category, index) => {
          const isCategoryOpen = categoryStates[category] || false;

          return (
            <div
              className="parent-group"
              id={`parent-group-${category.replaceAll(" ", "-")}-${index}`}
              key={index} // This key was correct
            >
              <div className="parent-group">
                <div
                  className="group-card"
                  id={`group-card-${category.replaceAll(" ", "-")}`}
                  onClick={() => toggleCategory(category)}
                >
                  <div className="group-title" id={`external-${index}`}>
                    {isCategoryOpen ? (
                      <FontAwesomeIcon icon={faFolderOpen} />
                    ) : (
                      <FontAwesomeIcon icon={faFolder} />
                    )}{" "}
                    {category}
                  </div>
                  <div className="group-card-chevron">
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      style={{
                        transform: isCategoryOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "0.3s ease",
                      }}
                    />
                  </div>
                </div>

                {/* --- LAYERS CONTAINER --- */}
                <div
                  className={`inner-group-container`}
                  id={`inner-group-container-${category.replaceAll(" ", "-")}`}
                  // FIX: Control display via State
                  style={{ display: isCategoryOpen ? "block" : "none" }}
                >
                  {externalData[category].map((item) => (
                    // FIX: ADDED UNIQUE KEY HERE
                    <div className="parent-group" key={item.name}>
                      <div
                        className="layer-card"
                        id={`layer-card-${item.name.replaceAll(" ", "-")}`}
                      >
                        <div
                          className="layer-card-content"
                          id="layer-card-external"
                        >
                          <div
                            className="layer-switch-container"
                            id={`${category}-layer-switch`}
                          >
                            <div className={`${item.name}-switch`}>
                              <GreenSwitch
                                id={`${category}-${item.name}-switch`}
                                checked={
                                  itemStates[item.name]?.enabled || false
                                }
                                onChange={() => handleChange(item)}
                                inputProps={{ "aria-label": "controlled" }}
                              />
                            </div>
                          </div>

                          <div className="layer-title-container-external">
                            {item.title}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ExternalComponentsLayers;
